@if (!graphId()) {
  <section class="cat-graphs-list" data-testid="cat-graphs-list">
    <header class="page-header">
      <h2>CAT graphs</h2>
      <p class="page-intro">
        Each graph shows how a finding follows from its premises, and links every premise to its evidence on the plans
        or in the code.
      </p>
    </header>

    @if (listLoading()) {
      <mat-progress-bar mode="indeterminate" aria-label="Loading the project's graphs"></mat-progress-bar>
    } @else if (listError()) {
      <p class="notice notice--error" role="alert">{{ listError() }}</p>
    } @else if (summaries().length === 0) {
      <p class="notice" data-testid="cat-graphs-empty">This project has no CAT graphs yet.</p>
    } @else {
      <ul class="graph-cards">
        @for (summary of summaries(); track summary.getGraphId()) {
          <li>
            <a class="graph-card" data-testid="cat-graph-link" [routerLink]="graphLink(summary.getGraphId())">
              <span class="graph-card__title">{{ summary.getTitle() || summary.getGraphId() }}</span>
              @if (summary.getSubtitle()) {
                <span class="graph-card__subtitle">{{ summary.getSubtitle() }}</span>
              }
              <span class="graph-card__counts">
                {{ summary.getNodeCount() }} claims · {{ summary.getGateCount() }} steps ·
                {{ summary.getArgumentCount() }} {{ summary.getArgumentCount() === 1 ? 'argument' : 'arguments' }}
              </span>
            </a>
          </li>
        }
      </ul>
    }
  </section>
} @else {
  <section class="cat-graph-view" data-testid="cat-graph-view">
    <header class="page-header">
      <a mat-button class="back-link" [routerLink]="listLink()">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
        All graphs
      </a>
      @if (graph(); as loaded) {
        <h2 data-testid="cat-graph-title">{{ loaded.getTitle() || loaded.getGraphId() }}</h2>
        @if (loaded.getSubtitle()) {
          <p class="page-intro">{{ loaded.getSubtitle() }}</p>
        }
      } @else {
        <h2>{{ graphId() }}</h2>
      }
    </header>

    @if (graphLoading()) {
      <mat-progress-bar mode="indeterminate" aria-label="Loading the graph"></mat-progress-bar>
    } @else if (graphError()) {
      <p class="notice notice--error" role="alert">{{ graphError() }}</p>
    } @else if (graph()) {
      <div class="toolbar">
        <mat-button-toggle-group aria-label="Show the graph as" [value]="mode()" (change)="setMode($event.value)">
          <mat-button-toggle value="picture" [disabled]="!picture()" data-testid="cat-graph-mode-picture">
            Picture
          </mat-button-toggle>
          <mat-button-toggle value="list" data-testid="cat-graph-mode-list">List</mat-button-toggle>
        </mat-button-toggle-group>

        @if (mode() === 'picture' && picture()) {
          <div class="zoom" role="group" aria-label="Zoom">
            <button mat-icon-button type="button" aria-label="Zoom out" matTooltip="Zoom out" (click)="zoomOut()">
              <mat-icon>zoom_out</mat-icon>
            </button>
            <button mat-icon-button type="button" aria-label="Zoom in" matTooltip="Zoom in" (click)="zoomIn()">
              <mat-icon>zoom_in</mat-icon>
            </button>
            <button mat-button type="button" [attr.aria-pressed]="zoom() === 'fit'" (click)="fitWidth()">
              Fit width
            </button>
          </div>
        }
      </div>

      <p class="notice notice--quiet">
        Clicking a piece of evidence opens the plan sheet with its regions highlighted. The regions were recorded when
        the graph was made, so they may be out of place if the plans were uploaded again since.
      </p>

      @if (pictureError()) {
        <p class="notice notice--warning" role="status" data-testid="cat-graph-picture-unavailable">
          {{ pictureError() }}
        </p>
      }
      <p class="visually-hidden" aria-live="polite">{{ announcement() }}</p>

      @if (selectedClaim(); as claim) {
        <aside class="selected-claim" aria-label="Selected claim" data-testid="cat-graph-selected-claim">
          <div class="selected-claim__kind">
            {{ claim.kind }}
            @if (claim.role) {
              · {{ claim.role }}
            }
          </div>
          <p class="selected-claim__statement">{{ claim.statement || claim.summary }}</p>
          <button mat-icon-button type="button" aria-label="Clear the selection" (click)="clearSelection()">
            <mat-icon>close</mat-icon>
          </button>
        </aside>
      }

      @if (mode() === 'picture') {
        @if (pictureLoading()) {
          <mat-progress-bar mode="indeterminate" aria-label="Loading the picture"></mat-progress-bar>
        }
        @if (pictureHtml(); as html) {
          <div class="picture-scroller">
            <!-- One handler for every chip and box; see onPictureClick. Chips are focusable links. -->
            <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
            <div
              #picture
              class="picture"
              data-testid="cat-graph-picture"
              role="region"
              [attr.aria-label]="pictureLabel()"
              [style.width.px]="pictureWidth()"
              [innerHTML]="html"
              (click)="onPictureClick($event)"
              (keydown)="onPictureKeydown($event)"
            ></div>
          </div>
        }
      } @else {
        <div class="claims" data-testid="cat-graph-claims">
          @for (argument of arguments(); track argument.argumentId) {
            <section class="argument">
              @if (argument.heading) {
                <h3>{{ argument.heading }}</h3>
              }
              @if (argument.caption) {
                <p class="argument__caption">{{ argument.caption }}</p>
              }
              <ol class="claim-list">
                @for (claim of argument.claims; track claim.nodeId) {
                  <li class="claim" [class.claim--selected]="selectedClaim() === claim" data-testid="cat-graph-claim">
                    <div class="claim__kind">
                      {{ claim.kind }}
                      @if (claim.role) {
                        · {{ claim.role }}
                      }
                    </div>
                    <p class="claim__statement">{{ claim.statement || claim.summary }}</p>
                    @if (claim.source) {
                      <p class="claim__source">{{ claim.source }}</p>
                    }
                    @if (claim.evidence.length) {
                      <ul class="evidence-list" aria-label="Evidence">
                        @for (evidence of claim.evidence; track $index) {
                          <li>
                            @if (evidence.citationIds.length) {
                              <button
                                mat-stroked-button
                                type="button"
                                class="evidence-button"
                                data-testid="cat-graph-evidence"
                                [matTooltip]="evidence.description"
                                (click)="selectAndOpenEvidence(claim, evidence.citationIds)"
                              >
                                {{ evidence.label }}
                              </button>
                            } @else {
                              <span class="evidence-text" [matTooltip]="evidence.description">
                                {{ evidence.label }}
                                @if (evidence.external) {
                                  ({{ evidence.external }})
                                }
                              </span>
                            }
                          </li>
                        }
                      </ul>
                    }
                  </li>
                }
              </ol>
            </section>
          }
        </div>
      }
    }
  </section>
}
